export const COMMERCIAL_FAQS = [
  { q: "How do you size a commercial solar system?", a: "Sizing starts with usage: 12 months of bills, energy charges, demand charges and your tariff. Available roof or land sets an upper bound, but utility data and interconnection limits usually shape the final design. Try the calculator with your bill total, then request an assessment to confirm assumptions." },
  { q: "Does my roof need to be replaced first?", a: "Roof condition matters more than age alone. A roof review checks remaining life, structure and drainage; if roof work is needed, coordinating it with the solar project avoids removing and reinstalling panels later. See the roofing service page." },
  { q: "I lease the building. Can I still go solar?", a: "Often yes, but permission and terms depend on your lease. Select “Tenant” in the audit form and the review will include the authorization questions your landlord conversation needs." },
  { q: "Cash purchase, financing or a PPA — which is right?", a: "Ownership keeps tax benefits and long-term savings with you; financing spreads cost over time; a PPA buys the power instead of the system and leaves ownership incentives with the system owner. The calculator compares the three paths — financing figures need an actual offer." },
  { q: "What incentive eligibility applies to my project?", a: "Investment-credit rules (IRC Section 48E), domestic-content bonuses and foreign-entity rules change frequently and depend on timing, basis and tax capacity. We model scenarios; your tax adviser must confirm eligibility. Current reference: IRS Clean Electricity Investment Credit (reviewed October 2026)." },
  { q: "Can batteries reduce my demand charges?", a: "Sometimes. Savings depend on how your utility measures demand (15-minute vs monthly), ratchets, time-of-use periods and the battery's usable power and energy. See the Demand Control pillar in the calculator, then upload bills for a tariff-specific review." },
  { q: "How disruptive is installation?", a: "Most rooftop work proceeds in phases around your operating schedule. Roof penetration, electrical work and inspections are coordinated in advance; your assessment will map the sequence before anything is scheduled." },
  { q: "What is utility interconnection and why does it take time?", a: "Interconnection is your utility's review and permission to connect the system. Applications, studies and witness testing can take weeks to months depending on system size and utility queue — the assessment will include the expected steps for your project." },
  { q: "Who maintains the system and what warranties exist?", a: "Monitoring, cleaning and service keep production on track. Equipment warranties come from manufacturers; workmanship warranty terms will be documented with your proposal — see About for confirmed credential and warranty details." },
  { q: "Will solar power my building during an outage?", a: "Standard grid-connected systems shut down during outages for safety. Backup requires batteries and a compatible electrical configuration sized for the loads that matter. See the storage section or ask about a commercial storage review." },
  { q: "How long does a project take from assessment to operation?", a: "Timelines depend on design, permitting, interconnection and scheduling. After your bill review, you'll receive the expected sequence and milestones for your property — we confirm timing rather than promise dates." },
  { q: "What do you need from me to start?", a: "A recent electric bill (PDF, or a clear photo of it), your property address and the roof or land details you know. Upload 1–12 bills in the audit form — account and payment numbers can be redacted." },
];

export const SERVICES = [
  {
    id: "commercial",
    icon: "▦",
    name: "Commercial solar",
    tag: "FOR BUSINESS",
    audience: "business",
    description: "Explore rooftop, carport and ground-mount solar around your facility, operating schedule and utility bill.",
    question: "What type of property are you planning for?",
    options: ["Warehouse / industrial", "Retail / office", "Multifamily / hospitality", "School / public facility"],
    faq: "What determines the right system size?",
    answer: "Available roof or land is only the start. Interval usage, utility tariffs, ownership and interconnection limits shape the design.",
    benefits: [
      "Turn idle roof, carport or land into working infrastructure",
      "Target the operating demand where your tariff hits hardest",
      "Plan around your schedule and your bill, with assumptions you can inspect",
    ],
    fit: ["You pay meaningful energy or demand charges", "You control the roof or land where a system would live"],
    process: [
      "Review your bill and interval data with a tariff-aware assessment",
      "Explore siting concepts on your actual roof or land",
      "Confirm ownership, incentive and interconnection assumptions with your advisers",
      "Move to engineering review with a complete project record",
    ],
  },
  {
    id: "home",
    icon: "⌂",
    name: "Home solar",
    tag: "FOR HOMEOWNERS",
    audience: "home",
    description: "Visualize a rooftop array that considers how your home looks and how your household uses energy.",
    question: "What is your typical monthly electric bill?",
    options: ["Under $200", "$200–$399", "$400–$699", "$700 or more"],
    faq: "Can you size solar from my roof alone?",
    answer: "Roof size helps illustrate what might fit. Your electricity use, shading and utility rules determine a useful system size.",
    benefits: [
      "Put your roof to work for the people under it",
      "Plan around how your household actually uses energy",
      "See storage as an option, never an automatic upsell",
    ],
    fit: ["You own the home or can authorize roof work", "Your electric bill gives the design something to work with"],
    process: [
      "Share your address, bill and roof basics",
      "Mark a roof plane or confirm your building to shape the layout",
      "Choose the backup priorities that matter to your household",
      "Request an assessment with your concept attached",
    ],
  },
  {
    id: "powerwall",
    icon: "▯",
    name: "Tesla Powerwall",
    tag: "STORE YOUR ENERGY",
    audience: "home",
    description: "Explore battery backup for a compatible property and select the everyday loads you want to prioritize.",
    question: "What matters most during an outage?",
    options: ["Refrigeration and essentials", "Home office and connectivity", "Comfort and cooling", "A broader backup plan"],
    faq: "Does one battery run my whole property?",
    answer: "Backup depends on power, energy capacity, starting loads and electrical configuration. A qualified system review determines the appropriate equipment.",
    benefits: [
      "Keep essentials running through outages",
      "Shift solar energy into the evening hours",
      "Pair circuit-level priorities with a compatible electrical system",
    ],
    fit: ["A compatible electrical service with space for the equipment", "Clear priorities for what stays on during an outage"],
    process: [
      "Tell us what matters most during an outage",
      "Review electrical service, starting loads and backup scope",
      "Get a system review matched to your property",
      "For larger or three-phase facilities, request an engineered commercial storage review instead",
    ],
  },
  {
    id: "roofing",
    icon: "⌁",
    name: "Roofing",
    tag: "START WITH A STRONG FOUNDATION",
    audience: "home",
    description: "Coordinate roof readiness with your future energy system. Explore replacement and repair before adding panels.",
    question: "What is the condition of your roof?",
    options: ["Good condition", "Near replacement", "Repair or leak concern", "Not sure"],
    faq: "Should I replace my roof before solar?",
    answer: "A roof inspection checks remaining life, condition and structure. Coordinating necessary roof work can avoid later panel removal.",
    benefits: [
      "Coordinate roof readiness before panels arrive",
      "Address repair or replacement on its own timeline",
      "Avoid the cost of removing and reinstalling panels later",
    ],
    fit: ["A roof age or condition you can describe", "A solar or storage plan somewhere in the picture"],
    process: [
      "Describe roof condition, age and history",
      "Inspect remaining life, structure and drainage",
      "Sequence roof work with your energy project",
      "Build one coordinated project plan",
    ],
  },
  {
    id: "solar-roof",
    icon: "▱",
    name: "Tesla Solar Roof",
    tag: "AN INTEGRATED ROOF",
    audience: "home",
    description: "Explore a solar roof concept or request service for an existing Tesla Solar Roof installation.",
    question: "What would you like help with?",
    options: ["Explore availability", "Existing Solar Roof service", "Repair or inspection", "Compare with solar panels"],
    faq: "Is Solar Roof available for every project?",
    answer: "Availability, installer capability and project suitability must be confirmed. This visual concept does not establish product availability.",
    benefits: [
      "Explore an integrated roof concept for replacement or new builds",
      "Request service for an existing installation",
      "Compare honestly with panels-plus-roof alternatives",
    ],
    fit: ["You are replacing a roof or building new", "You want the roof surface itself to generate"],
    process: [
      "Share your project stage and roof details",
      "Confirm product availability and installer capability for your area",
      "Compare Solar Roof with panel-based scenarios",
      "Move forward only where the product is genuinely available",
    ],
  },
  {
    id: "construction",
    icon: "⊞",
    name: "New construction",
    tag: "DESIGN IT IN FROM DAY ONE",
    audience: "business",
    description: "Bring roof, solar, storage and electrical planning together before construction decisions become expensive changes.",
    question: "What stage is your project in?",
    options: ["Early concept", "Plans in progress", "Permitting", "Under construction"],
    faq: "When should I start energy planning?",
    answer: "Early coordination helps reserve equipment space and plan roof layouts, conduit routes and electrical capacity.",
    benefits: [
      "Design energy into the build before decisions get expensive",
      "Reserve electrical capacity and roof space early",
      "One plan for roof, solar, storage and the electrical service",
    ],
    fit: ["A project anywhere from concept through construction", "Builder or owner involvement in energy decisions"],
    process: [
      "Share your plans and current stage",
      "Coordinate roof layout, conduit routes and equipment space",
      "Align electrical capacity with the loads you expect",
      "Hand your builder a complete energy scope",
    ],
  },
  {
    id: "hvac",
    icon: "❋",
    name: "HVAC & air quality",
    tag: "COMFORT WITH LESS WASTE",
    audience: "business",
    description: "Explore cooling, comfort and air-quality improvements alongside your broader energy plan.",
    question: "What is your main comfort concern?",
    options: ["High cooling bills", "System replacement", "Repairs", "Humidity / indoor air quality"],
    faq: "Why review HVAC with solar?",
    answer: "Reducing electrical loads can change how much solar or storage your property needs. Actual savings depend on equipment and usage.",
    benefits: [
      "Cut waste before you size generation",
      "Improve comfort and indoor air quality together",
      "Let real loads drive your solar and storage design",
    ],
    fit: ["High cooling bills or aging equipment", "Comfort problems you can describe"],
    process: [
      "Describe your comfort concern",
      "Review equipment condition and usage patterns",
      "Size improvements against real loads",
      "Fold the results into your energy plan",
    ],
  },
  {
    id: "insulation",
    icon: "≋",
    name: "Spray foam insulation",
    tag: "REDUCE YOUR ENERGY LOAD",
    audience: "home",
    description: "Explore attic insulation and air sealing as part of a coordinated comfort and efficiency review.",
    question: "Which area needs attention?",
    options: ["Attic", "New construction", "Commercial space", "Not sure"],
    faq: "Is spray foam right for every building?",
    answer: "Moisture, ventilation, building assemblies and code requirements must be evaluated before choosing an insulation approach.",
    benefits: [
      "Reduce the load you pay for every hour",
      "Seal the envelope before adding generation",
      "Comfort that shows up in every season",
    ],
    fit: ["An attic or envelope you can access", "Rooms that are hard to keep comfortable"],
    process: [
      "Identify the areas that need attention",
      "Evaluate moisture, ventilation and assembly fit",
      "Recommend the right insulation approach",
      "Measure the effect at your next review",
    ],
  },
  {
    id: "ev",
    icon: "ϟ",
    name: "EV charging",
    tag: "POWER THE NEXT MILE",
    audience: "home",
    description: "Plan charging around parking, driving habits, electrical capacity and the energy your roof can produce.",
    question: "Where will the charger be installed?",
    options: ["Home garage", "Business parking", "Multifamily parking", "Fleet facility"],
    faq: "Will I need an electrical upgrade?",
    answer: "A load calculation and site assessment determine available capacity, circuit design and any necessary upgrades.",
    benefits: [
      "Charge at the pace your parking supports",
      "Plan electrical capacity before you buy the charger",
      "Pair charging with the energy your roof can produce",
    ],
    fit: ["A parking spot you can electrify", "Driving patterns you can describe and size around"],
    process: [
      "Describe parking and electrical setup",
      "Calculate available capacity and circuit design",
      "Select equipment and placement",
      "Install and register your charging plan",
    ],
  },
  {
    id: "smart-panel",
    icon: "⊡",
    name: "SPAN smart panel",
    tag: "CONTROL THE CIRCUITS THAT MATTER",
    audience: "home",
    description: "Explore circuit-level insight and backup priorities within a compatible electrical system.",
    question: "What is your main goal?",
    options: ["Energy visibility", "Backup priorities", "Electrical modernization", "New construction"],
    faq: "Can a smart panel work with my battery?",
    answer: "Compatibility, service configuration and installer requirements must be confirmed for the proposed equipment combination.",
    benefits: [
      "See energy circuit by circuit",
      "Set backup priorities that actually hold",
      "Modernize the panel behind your whole energy plan",
    ],
    fit: ["An electrical modernization on your list", "Battery or backup goals in play"],
    process: [
      "Review your current electrical service",
      "Map the circuits that matter most",
      "Confirm compatibility with your battery plans",
      "Install with circuit-level control configured",
    ],
  },
  {
    id: "supercharger",
    icon: "∩",
    name: "Supercharger sites",
    tag: "FOR COMMERCIAL HOSTS",
    audience: "business",
    description: "Explore site planning, electrical needs and feasibility for a commercial charging location.",
    question: "What is your site type?",
    options: ["Retail / shopping center", "Hospitality", "Travel destination", "Fleet / other commercial"],
    faq: "Does an assessment guarantee a charging site?",
    answer: "No. Utility capacity, site economics, program selection, permitting and equipment availability determine feasibility.",
    benefits: [
      "Evaluate your property as a charging destination",
      "Understand utility capacity before you commit",
      "Plan around the traffic you already have",
    ],
    fit: ["Retail, hospitality or fleet parking at scale", "Authority to explore a site agreement"],
    process: [
      "Describe the site and its parking",
      "Screen utility capacity and interconnection",
      "Model site economics and program fit",
      "Coordinate feasibility steps before any commitment",
    ],
  },
  {
    id: "care",
    icon: "✳",
    name: "Service & maintenance",
    tag: "KEEP YOUR ENERGY SYSTEM WORKING",
    audience: "business",
    description: "Request diagnostics, cleaning, repairs or monitoring support for an existing solar or storage system.",
    question: "What kind of help do you need?",
    options: ["Production issue", "Cleaning", "Battery / inverter issue", "Roof / mounting issue"],
    faq: "Can I request service on an existing system?",
    answer: "Share your equipment details and issue. Availability and scope are confirmed after reviewing the system and service area.",
    benefits: [
      "Restore production when something slips",
      "Keep equipment clean and monitored",
      "Diagnose battery, inverter or roof-mounting issues",
    ],
    fit: ["An existing solar or storage system", "A symptom you can describe or share"],
    process: [
      "Describe the issue and your equipment",
      "Diagnose remotely where possible",
      "Schedule the visit your system needs",
      "Confirm production after the work",
    ],
  },
];

export const BELTS = [
  {
    name: "White belt",
    title: "Know your bill",
    tagline: "Build confidence, one step at a time.",
    body: "Learn the difference between energy (kWh), demand (kW), fixed charges and time-of-use rates. Bring 12 months of bills to a project assessment.",
    next: ["Pull your last 3 bills", "Circle the kWh and any demand lines", "Note your rate schedule name"],
  },
  {
    name: "Yellow belt",
    title: "Understand your roof",
    tagline: "Build confidence, one step at a time.",
    body: "Roof age, structure, pitch, azimuth, shade and obstructions all affect a useful layout. Photos and dimensions are a starting point, not a structural survey.",
    next: ["Estimate roof age", "Photograph each roof face", "List known obstructions"],
  },
  {
    name: "Green belt",
    title: "Connect solar + storage",
    tagline: "Build confidence, one step at a time.",
    body: "Solar produces energy; batteries move energy across time and can support backup when correctly configured. Power and energy capacity are different sizing questions.",
    next: ["List what must stay on in an outage", "Note your electrical service size", "Mark where equipment could live"],
  },
  {
    name: "Brown belt",
    title: "Compare the economics",
    tagline: "Build confidence, one step at a time.",
    body: "Compare project cost, ownership, financing, maintenance, tariff assumptions and any verified incentives. Avoid treating estimates as guaranteed returns.",
    next: ["Try the calculators with your numbers", "Ask your adviser about eligibility", "Compare ownership with financed paths"],
  },
  {
    name: "Black belt",
    title: "Plan your next move",
    tagline: "Build confidence, one step at a time.",
    body: "Assemble bills, property details, photos and priorities. A qualified project assessment confirms engineering, service coverage and a proposal.",
    next: ["Upload your bill for a bill-based audit", "Request an assessment", "Bring your priorities to the review"],
  },
];

export const HOMEPAGE_FAQ = [
  {
    q: "Can I see panels on my actual building?",
    a: "Yes. Add a roof photo and mark a roof plane to try a concept overlay here. The live version will also let you confirm the building on Google Maps where imagery and solar data are available.",
  },
  {
    q: "Will solar keep the lights on during an outage?",
    a: "Standard grid-connected solar usually shuts down during an outage. A compatible battery and backup configuration can support selected loads. Your electrical design determines what can run and for how long.",
  },
  {
    q: "Can my business use Tesla Powerwall?",
    a: "It depends on electrical service, facility loads and product compatibility. A small compatible property may be a fit; larger facilities need a commercial storage assessment rather than a household battery assumption.",
  },
  {
    q: "How are savings estimated?",
    a: "Using your utility bills, tariff, demand profile, roof layout, equipment assumptions and project costs. Any tax incentives need a separate eligibility review. We do not turn a roof-size estimate into a guaranteed savings claim.",
  },
  {
    q: "Where are installation services available?",
    a: "Project availability is confirmed during your assessment. Educational resources can cover many states; installation coverage depends on qualified crews, licensing and the specific service requested.",
  },
];
